"use client";

import { c as _c } from "react/compiler-runtime";
import { IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconAlertInfoOutline18, IconArrowRotateClockwiseOutline18, IconCode2Outline18 } from "nucleo-ui-outline-18";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useOrganizationWallets } from "@/hooks/useOrganizationWallets";
import { useOrganizations } from "@/hooks/useOrganizations";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import DataRow from "../DataRow";
import { getArchetypeConfig } from "./registry";
import type { TriggerArchetypeProps } from "./types";
import { useTriggerDataLoader } from "./useTriggerDataLoader";
const ENTITY_LABELS: Record<string, {
  singular: string;
  plural: string;
  searchPlaceholder: string;
}> = {
  "shopify-order-created": {
    singular: "order",
    plural: "orders",
    searchPlaceholder: "Search by order number or email..."
  },
  "shopify-order-paid": {
    singular: "paid order",
    plural: "paid orders",
    searchPlaceholder: "Search by order number or email..."
  },
  "shopify-inventory-level-updated": {
    singular: "inventory item",
    plural: "inventory items",
    searchPlaceholder: "Search by product name or SKU..."
  }
};
const DEFAULT_LABEL = {
  singular: "item",
  plural: "items",
  searchPlaceholder: "Search..."
};
const SHOPIFY_ORDER_TRIGGERS = new Set(["shopify-order-created", "shopify-order-paid"]);
const SHOPIFY_INVENTORY_TRIGGERS = new Set(["shopify-inventory-level-updated"]);

// ============================================================================
// Rich Row Components
// ============================================================================

/** Shared row wrapper with radio button, clickable content, and JSON toggle */
function EntityRow(t0) {
  const $ = _c(29);
  const {
    data,
    value,
    selected,
    onSelect,
    children
  } = t0;
  const [showRaw, setShowRaw] = useState(false);
  let t1;
  if ($[0] !== onSelect || $[1] !== value) {
    t1 = () => onSelect(value);
    $[0] = onSelect;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== selected || $[4] !== t1 || $[5] !== value) {
    t2 = <RadioGroupItem value={value} checked={selected} onClick={t1} className="flex-shrink-0" />;
    $[3] = selected;
    $[4] = t1;
    $[5] = value;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== onSelect || $[8] !== value) {
    t3 = () => onSelect(value);
    $[7] = onSelect;
    $[8] = value;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== children || $[11] !== t3) {
    t4 = <button type="button" onClick={t3} className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">{children}</button>;
    $[10] = children;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== showRaw) {
    t5 = () => setShowRaw(!showRaw);
    $[13] = showRaw;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = showRaw ? "Hide JSON" : "View JSON";
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IconCode2Outline18 className="h-3 w-3" />;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t6) {
    t8 = <button type="button" onClick={t5} className="hover:bg-ds-surface-hover hover:text-ds-text-muted dark:hover:bg-ds-neutral-800 dark:hover:text-ds-text-disabled flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-zinc-300 transition-colors" title={t6}>{t7}</button>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t2 || $[20] !== t4 || $[21] !== t8) {
    t9 = <div className="flex items-center gap-2.5 py-2">{t2}{t4}{t8}</div>;
    $[19] = t2;
    $[20] = t4;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== data || $[24] !== showRaw) {
    t10 = showRaw && <pre className="text-ds-text-subtle dark:bg-ds-neutral-900 dark:text-ds-text-disabled text-2xs mb-1 ml-8 max-h-32 overflow-auto rounded-lg bg-zinc-50 p-2 font-mono leading-relaxed">{JSON.stringify(data, null, 2)}</pre>;
    $[23] = data;
    $[24] = showRaw;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t9) {
    t11 = <div>{t9}{t10}</div>;
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}

/** Compact row for Shopify order events */
function ShopifyOrderRow(props) {
  const $ = _c(21);
  const {
    data
  } = props;
  const order = data.order as Record<string, unknown> | undefined;
  const lineItems = (order?.line_items ?? order?.lineItems) as Record<string, unknown>[] | undefined;
  const orderName = data._orderName as string || order?.name as string || "";
  const rawPrice = (order?.total_price ?? order?.totalPrice) as string | undefined;
  const totalPrice = data._totalPrice as string || (rawPrice ? `$${rawPrice}` : "");
  const productTitle = data._productTitle as string || lineItems?.[0]?.title as string || "";
  let t0;
  if ($[0] !== data._status || $[1] !== order?.financialStatus || $[2] !== order?.financial_status) {
    t0 = data._status as string || ((order?.financial_status ?? order?.financialStatus) as string)?.toLowerCase() || "";
    $[0] = data._status;
    $[1] = order?.financialStatus;
    $[2] = order?.financial_status;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const status = t0;
  let t1;
  if ($[4] !== orderName) {
    t1 = <span className="text-ds-text-primary text-sm font-medium">{orderName}</span>;
    $[4] = orderName;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== totalPrice) {
    t2 = <span className="text-ds-text-muted text-xs">{totalPrice}</span>;
    $[6] = totalPrice;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = `text-3xs font-medium ${status === "paid" ? "text-emerald-500" : "text-amber-500"}`;
  let t4;
  if ($[8] !== status || $[9] !== t3) {
    t4 = <span className={t3}>{status}</span>;
    $[8] = status;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t4) {
    t5 = <div className="flex items-center gap-2">{t1}{t2}{t4}</div>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== productTitle) {
    t6 = productTitle && <span className="text-ds-text-disabled dark:text-ds-text-muted text-xs">{productTitle}</span>;
    $[15] = productTitle;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== props || $[18] !== t5 || $[19] !== t6) {
    t7 = <EntityRow {...props}>{t5}{t6}</EntityRow>;
    $[17] = props;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}

/** Compact row for Shopify inventory events */
function ShopifyInventoryRow(props) {
  const $ = _c(22);
  const {
    data
  } = props;
  const invLevel = (data.inventoryLevel ?? data.inventory_level) as Record<string, unknown> | undefined;
  const productTitle = data._productTitle as string || "Inventory Item";
  const variantTitle = data._variantTitle as string | null ?? null;
  const sku = data._sku as string | null ?? null;
  const available = data._available as number ?? invLevel?.available as number ?? 0;
  const itemId = invLevel?.inventory_item_id as string ?? "";
  let t0;
  if ($[0] !== data._productTitle || $[1] !== itemId) {
    t0 = itemId && !data._productTitle ? itemId.replace(/^gid:\/\/shopify\/InventoryItem\//, "#") : null;
    $[0] = data._productTitle;
    $[1] = itemId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const shortId = t0;
  let t1;
  if ($[3] !== shortId) {
    t1 = shortId && <span className="text-ds-text-disabled dark:text-ds-text-muted font-normal"> {shortId}</span>;
    $[3] = shortId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== variantTitle) {
    t2 = variantTitle && <span className="text-ds-text-disabled dark:text-ds-text-muted font-normal"> / {variantTitle}</span>;
    $[5] = variantTitle;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== productTitle || $[8] !== t1 || $[9] !== t2) {
    t3 = <span className="text-ds-text-primary text-sm font-medium">{productTitle}{t1}{t2}</span>;
    $[7] = productTitle;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== available) {
    t4 = <span className="text-ds-text-muted ml-auto text-xs tabular-nums">{available} in stock</span>;
    $[11] = available;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <div className="flex items-center gap-2">{t3}{t4}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== sku) {
    t6 = sku && <span className="text-ds-text-disabled dark:text-ds-text-muted text-2xs">SKU: {sku}</span>;
    $[16] = sku;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== props || $[19] !== t5 || $[20] !== t6) {
    t7 = <EntityRow {...props}>{t5}{t6}</EntityRow>;
    $[18] = props;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * EntitySearch archetype — search and select a specific entity as test data.
 *
 * Used for triggers where the user selects a specific record: Shopify orders,
 * inventory items, etc.
 */
export function EntitySearch({
  triggerType,
  triggerPayload,
  onRun,
  supportsLiveTest,
  workflowId,
  connectorId
}: TriggerArchetypeProps) {
  const labels = ENTITY_LABELS[triggerType] ?? DEFAULT_LABEL;
  const config = getArchetypeConfig(triggerType);
  const {
    selectedOrgId
  } = useOrganizations();
  const {
    wallets
  } = useOrganizationWallets(selectedOrgId ?? undefined);
  const {
    results,
    isLoading,
    error,
    reload
  } = useTriggerDataLoader(triggerType, triggerPayload, supportsLiveTest, workflowId, {
    fetchFallbackData: config.fetchFallbackData,
    fallbackContext: {
      wallets: wallets?.map(w => ({
        id: w.id,
        address: w.address
      })) ?? [],
      connectorId
    }
  });
  const [selectedIndex, setSelectedIndex] = useState<string>("0");
  const [searchQuery, setSearchQuery] = useState("");
  const isShopifyOrder = SHOPIFY_ORDER_TRIGGERS.has(triggerType);
  const isShopifyInventory = SHOPIFY_INVENTORY_TRIGGERS.has(triggerType);

  // Pre-compute stringified representations for efficient per-keystroke filtering
  const stringifiedResults = useMemo(() => results.map(item => JSON.stringify(item).toLowerCase()), [results]);
  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) return results;
    const query = searchQuery.toLowerCase();
    return results.filter((_, i) => stringifiedResults[i].includes(query));
  }, [results, searchQuery, stringifiedResults]);

  // Reset selection and notify parent when filtered results change (data load or search query)
  useEffect(() => {
    setSelectedIndex("0");
    if (filteredResults[0]) onRun(filteredResults[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onRun is a stable state setter
  }, [filteredResults]);

  // Notify parent when user changes selection
  useEffect(() => {
    const idx = Math.min(Number(selectedIndex), filteredResults.length - 1);
    const payload = filteredResults[idx];
    if (payload) onRun(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onRun is a stable state setter
  }, [selectedIndex]);
  if (isLoading) {
    return <div className="border-ds-border-default dark:bg-ds-neutral-900/40 flex items-center justify-center gap-2 rounded-xl border bg-zinc-50/60 p-8">
        <PixelLoader size="sm" color="blue" />
        <span className="text-ds-text-muted text-sm">Loading {labels.plural}...</span>
      </div>;
  }
  if (error && results.length === 0) {
    return <div className="space-y-3">
        <div className="border-ds-border-default dark:bg-ds-neutral-900/40 flex items-start gap-2 rounded-xl border bg-zinc-50/60 p-4">
          <IconAlertInfoOutline18 className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
          <p className="text-ds-text-secondary text-sm">{error}</p>
        </div>
        <button type="button" onClick={reload} className="label label-xs hover:text-ds-text-secondary flex items-center gap-2 transition-colors dark:hover:text-zinc-200">
          <IconArrowRotateClockwiseOutline18 className="h-3 w-3" />
          Retry
        </button>
      </div>;
  }
  return <div className="space-y-3">
      <div className="relative">
        <IconMagnifyingGlass className="text-ds-text-disabled absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={labels.searchPlaceholder} className="border-ds-border-default text-ds-text-primary placeholder:text-ds-text-disabled dark:bg-ds-neutral-900/40 w-full rounded-xl border bg-zinc-50/60 py-2.5 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
      </div>
      {filteredResults.length === 0 ? <p className="text-ds-text-muted py-4 text-center text-sm">No {labels.plural} match your search.</p> : <RadioGroup value={selectedIndex} onValueChange={setSelectedIndex} className="max-h-64 gap-0 overflow-y-auto">
          <div className="divide-ds-neutral-100 dark:divide-ds-neutral-800 divide-y">
            {filteredResults.map((data, index) => isShopifyOrder ? <ShopifyOrderRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isShopifyInventory ? <ShopifyInventoryRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : <DataRow key={index} data={data} label={`${labels.singular} ${index + 1}`} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} />)}
          </div>
        </RadioGroup>}
    </div>;
}